import { useId, useState } from "react";
import { Button } from "./Button";
import { SelectField, TextAreaField, TextField } from "./form/Field";
import { FormError } from "./form/FormError";
import { reportDeal } from "../api/writes";
import { messageForError, REPORT_MESSAGES } from "../lib/api-messages";
import { useSubmitAction } from "../hooks/useSubmitAction";
import * as check from "../lib/validate";
import styles from "./ReportDealForm.module.css";
import type { DealReportRequest, ReportReason } from "../api/types";
import type { FieldErrors } from "../lib/form";
import type { SelectOption } from "./form/Field";

/**
 * Reporting a deal that has gone wrong (canon § 13).
 *
 * A buyer has no account, so this is the only channel through which Dealbid learns that an
 * approved deal expired, moved its price or broke its link. Two properties of the endpoint
 * shape the copy here, and neither may be softened:
 *
 *   - **a report is evidence, not a moderation action.** It is stored `open` and changes
 *     nothing about the deal. A deal strangers could take off the board by filing reports
 *     would be a free way to remove a competitor, so the confirmation says what actually
 *     happens rather than implying the listing has been dealt with;
 *   - **the reasons are a fixed list.** A free-text reason is unsortable in a moderation
 *     queue, and `other` on its own is a row nobody can act on — which is why a note is
 *     required with it.
 *
 * It is an inline panel rather than a modal. Nothing here needs to interrupt the page, and
 * a disclosure keeps the deal it belongs to visible and its own focus order intact.
 */

const REASONS: readonly SelectOption[] = [
  { value: "", label: "Choose a reason" },
  { value: "expired", label: "The deal has expired" },
  { value: "incorrect_price", label: "The price or discount is wrong" },
  { value: "misleading", label: "The offer is misleading" },
  { value: "broken_link", label: "The link doesn’t work" },
  { value: "not_as_described", label: "The product isn’t as described" },
  { value: "spam", label: "Spam or abuse" },
  { value: "other", label: "Something else" },
];

const MAX_DETAIL_LENGTH = 2000;

export function ReportDealForm({
  dealId,
  productName,
}: {
  dealId: string;
  productName: string;
}) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");
  const [reporterEmail, setReporterEmail] = useState("");

  const form = useSubmitAction<DealReportRequest, unknown>({
    prepare: () => {
      const errors: FieldErrors = {};
      if (reason === "") errors.reason = "Choose what’s wrong with this deal.";

      const detailProblem = check.optionalText(detail, MAX_DETAIL_LENGTH);
      if (detailProblem !== null) errors.detail = detailProblem;
      // Core requires this too. Saying so here means a buyer is told while the form is
      // still in front of them, rather than after a round trip.
      if (reason === "other" && detail.trim() === "") {
        errors.detail = "Describe the problem when the reason is “Something else”.";
      }

      const emailProblem = check.email(reporterEmail, { required: false });
      if (emailProblem !== null) errors.reporterEmail = emailProblem;

      if (Object.keys(errors).length > 0) return { ok: false, errors };

      return {
        ok: true,
        body: {
          reason: reason as ReportReason,
          detail: check.optionalValue(detail),
          reporterEmail: check.optionalValue(reporterEmail)?.toLowerCase() ?? null,
        },
      };
    },
    send: (body, signal) => reportDeal(dealId, body, signal),
  });

  if (form.status === "success") {
    return (
      <div className={styles.done} role="status">
        <p className={styles.doneTitle}>Report received.</p>
        <p className={styles.doneBody}>
          It goes to a person for review. A report never changes a deal on its own, so{" "}
          {productName} stays on the board until Dealbid checks it.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {open ? "Cancel report" : "Report this deal"}
      </button>

      {open ? (
        <form id={panelId} className={styles.panel} onSubmit={form.onSubmit} noValidate>
          <p className={styles.intro}>
            Tell Dealbid what’s wrong with <strong>{productName}</strong>. Reports go to a
            person; they don’t remove a deal automatically.
          </p>

          {form.status === "error" ? (
            <FormError
              title={messageForError(form.error, REPORT_MESSAGES)}
              requestId={form.error?.requestId}
            />
          ) : null}

          <SelectField
            name="reason"
            label="What’s wrong"
            options={REASONS}
            required
            value={reason}
            onChange={setReason}
            {...(form.fieldErrors.reason === undefined
              ? {}
              : { error: form.fieldErrors.reason })}
          />

          <TextAreaField
            name="detail"
            label="What happened"
            hint="Anything that helps someone check it quickly — the price you saw, what the page said."
            maxLength={MAX_DETAIL_LENGTH}
            required={reason === "other"}
            value={detail}
            onChange={setDetail}
            {...(form.fieldErrors.detail === undefined
              ? {}
              : { error: form.fieldErrors.detail })}
          />

          <TextField
            name="reporterEmail"
            label="Your email"
            type="email"
            inputMode="email"
            autoComplete="email"
            maxLength={254}
            hint="Only so someone can come back with a question. It is never shown publicly."
            value={reporterEmail}
            onChange={setReporterEmail}
            {...(form.fieldErrors.reporterEmail === undefined
              ? {}
              : { error: form.fieldErrors.reporterEmail })}
          />

          <Button type="submit" variant="secondary" disabled={form.status === "submitting"}>
            {form.status === "submitting" ? "Sending…" : "Send report"}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
